/**
 * RegistryToolSelector Component (AC-376)
 *
 * Allows users to select registry-enabled models as tools for their agents.
 * Displays available registry-enabled models with dedup fields and entry counts,
 * supporting multiple model selections.
 *
 * Features:
 * - Fetches registry-enabled models from API
 * - Displays loading and error states
 * - Shows model information (name, description, dedup fields, entry count)
 * - Supports multiple selections via checkbox-style cards
 * - Provides empty state guidance with link to Models page
 * - Preserves non-registry tools when updating selections
 */

import React, { useState, useEffect } from 'react';
import { Database, Check, AlertCircle, ExternalLink, Wrench, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AgentTool, RegistryEnabledModel, RegistryUseCase, RegistryCallStyle } from '../../types';
import apiService from '../../services/api';
import CustomSelect from '../common/CustomSelect';

interface RegistryToolSelectorProps {
  projectId: number;
  selectedTools: AgentTool[];
  onToolsChange: (tools: AgentTool[]) => void;
}

// Use case options with descriptions
const USE_CASE_OPTIONS: { value: RegistryUseCase; label: string; description: string }[] = [
  { value: 'deduplicate', label: 'Deduplicate Output', description: 'Check for duplicates and only output new entries' },
  { value: 'track', label: 'Track Seen Items', description: 'Check if items exist but do not auto-register' },
  { value: 'log', label: 'Log All Output', description: 'Store all output for history (no dedup)' },
  { value: 'full', label: 'Full Control', description: 'All tools available - use as needed' },
];

interface InjectedTool {
  name: string;
  desc: string;
  inputSchema: object;
  outputExample: object;
}

// Build a single-value input schema. The shape mirrors the example fields that
// the agent fills in (e.g. dedup key + any other model fields it knows about).
const buildValueSchema = (dedupFields: string[] | null | undefined): object => {
  const valueProps: Record<string, { type: string; description?: string }> = {};
  (dedupFields || []).forEach((f) => {
    valueProps[f] = { type: 'string', description: 'Dedup field' };
  });
  return {
    type: 'object',
    properties: {
      value: {
        type: 'object',
        description: "Output value as a dict with the model's fields",
        properties: valueProps,
        additionalProperties: true,
      },
    },
    required: ['value'],
  };
};

// Build a batch input schema (array of dicts with the model's fields).
const buildValuesSchema = (dedupFields: string[] | null | undefined): object => {
  const itemProps: Record<string, { type: string; description?: string }> = {};
  (dedupFields || []).forEach((f) => {
    itemProps[f] = { type: 'string', description: 'Dedup field' };
  });
  return {
    type: 'object',
    properties: {
      values: {
        type: 'array',
        maxItems: 100,
        items: {
          type: 'object',
          properties: itemProps,
          additionalProperties: true,
        },
        description: 'List of output values (max 100 items)',
      },
    },
    required: ['values'],
  };
};

const sampleDedupKey = (dedupFields: string[] | null | undefined): Record<string, string> => {
  const key: Record<string, string> = {};
  (dedupFields || []).forEach((f) => {
    key[f] = `<${f}>`;
  });
  return key;
};

// Generate the list of tools that get injected based on use case + call style.
// For non-'full' use cases the agent only sees ONE variant (batch or single)
// per operation — exposing both has been shown to cost reasoning tokens and
// push weaker models toward single-item loops. 'full' ignores call style and
// exposes all six tools.
//
// Each entry includes the input schema (what the agent must send) and an
// example output (the JSON shape returned back to the agent).
const getInjectedTools = (
  className: string,
  useCase: RegistryUseCase = 'deduplicate',
  dedupFields?: string[] | null,
  callStyle: RegistryCallStyle = 'batch',
): InjectedTool[] => {
  const valueSchema = buildValueSchema(dedupFields);
  const valuesSchema = buildValuesSchema(dedupFields);
  const dedupKey = sampleDedupKey(dedupFields);

  const allTools: Record<string, InjectedTool> = {
    check_and_register_batch: {
      name: `registry_check_and_register_batch_${className}`,
      desc: 'Check and register multiple entries (recommended)',
      inputSchema: valuesSchema,
      outputExample: {
        results: [
          { is_new: true, registered: true, dedup_key: dedupKey },
          { is_new: false, registered: false, dedup_key: dedupKey },
        ],
      },
    },
    check_and_register: {
      name: `registry_check_and_register_${className}`,
      desc: 'Check and register a single entry',
      inputSchema: valueSchema,
      outputExample: {
        is_new: true,
        registered: true,
        expires_at: '2026-12-31T23:59:59Z',
        dedup_key: dedupKey,
      },
    },
    check_exists_batch: {
      name: `registry_check_exists_batch_${className}`,
      desc: 'Check if multiple entries exist',
      inputSchema: valuesSchema,
      outputExample: {
        results: [
          { exists: false, registered_at: null, dedup_key: dedupKey },
          { exists: true, registered_at: '2026-04-01T10:00:00Z', dedup_key: dedupKey },
        ],
      },
    },
    check_exists: {
      name: `registry_check_exists_${className}`,
      desc: 'Check if a single entry exists',
      inputSchema: valueSchema,
      outputExample: {
        exists: false,
        registered_at: null,
        source_agent: null,
        expires_at: null,
        dedup_key: dedupKey,
      },
    },
    register_batch: {
      name: `registry_register_batch_${className}`,
      desc: 'Register multiple entries (skip check)',
      inputSchema: valuesSchema,
      outputExample: {
        registered_count: 1,
        already_existed_count: 1,
        results: [
          { success: true, already_existed: false, dedup_key: dedupKey },
          { success: true, already_existed: true, dedup_key: dedupKey },
        ],
      },
    },
    register: {
      name: `registry_register_${className}`,
      desc: 'Register a single entry (skip check)',
      inputSchema: valueSchema,
      outputExample: {
        success: true,
        already_existed: false,
        expires_at: '2026-12-31T23:59:59Z',
        dedup_key: dedupKey,
        dedup_key_hash: 'a1b2c3d4...',
      },
    },
  };

  const useBatch = callStyle !== 'single';
  switch (useCase) {
    case 'deduplicate':
      return useBatch ? [allTools.check_and_register_batch] : [allTools.check_and_register];
    case 'track':
      return useBatch ? [allTools.check_exists_batch] : [allTools.check_exists];
    case 'log':
      return useBatch ? [allTools.register_batch] : [allTools.register];
    case 'full':
      // 'full' deliberately ignores callStyle so power users get every tool
      return Object.values(allTools);
    default:
      return useBatch ? [allTools.check_and_register_batch] : [allTools.check_and_register];
  }
};

const RegistryToolSelector: React.FC<RegistryToolSelectorProps> = ({
  projectId,
  selectedTools,
  onToolsChange,
}) => {
  const [registryModels, setRegistryModels] = useState<RegistryEnabledModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedToolLists, setExpandedToolLists] = useState<Set<number>>(new Set());
  // Tracks which individual injected tool schemas are expanded.
  // Key format: `${modelId}:${toolName}` so each tool is independently toggleable.
  const [expandedToolSchemas, setExpandedToolSchemas] = useState<Set<string>>(new Set());

  // Fetch registry-enabled models on mount
  useEffect(() => {
    const fetchRegistryModels = async () => {
      setLoading(true);
      setError(null);
      try {
        const models = await apiService.getRegistryEnabledModels(projectId);
        setRegistryModels(models);
      } catch (err) {
        console.error('Failed to fetch registry-enabled models:', err);
        setError('Error loading registry models');
      } finally {
        setLoading(false);
      }
    };

    fetchRegistryModels();
  }, [projectId]);

  // Check if a model is selected
  const isModelSelected = (modelId: number): boolean => {
    return selectedTools.some(
      (tool) => tool.type === 'registry' && tool.registry_model_id === modelId
    );
  };

  // Get the use case for a selected model
  const getModelUseCase = (modelId: number): RegistryUseCase => {
    const tool = selectedTools.find(
      (t) => t.type === 'registry' && t.registry_model_id === modelId
    );
    return tool?.registry_use_case || 'deduplicate';
  };

  // Get the call style for a selected model. Defaults to 'batch' so legacy
  // tool records (created before this field existed) keep behaving like the
  // old "first listed = recommended" default.
  const getModelCallStyle = (modelId: number): RegistryCallStyle => {
    const tool = selectedTools.find(
      (t) => t.type === 'registry' && t.registry_model_id === modelId
    );
    return tool?.registry_call_style || 'batch';
  };

  // Count selected registry models
  const selectedRegistryCount = selectedTools.filter(
    (tool) => tool.type === 'registry' && tool.registry_model_id !== undefined
  ).length;

  // Handle model selection/deselection
  const handleModelToggle = (model: RegistryEnabledModel) => {
    const isSelected = isModelSelected(model.model_id);

    if (isSelected) {
      // Deselect: remove the registry tool for this model
      const updatedTools = selectedTools.filter(
        (tool) => !(tool.type === 'registry' && tool.registry_model_id === model.model_id)
      );
      onToolsChange(updatedTools);
      // Also collapse the tool list
      setExpandedToolLists((prev) => {
        const next = new Set(prev);
        next.delete(model.model_id);
        return next;
      });
    } else {
      // Select: add a new registry tool for this model with default use case
      // and default call style ('batch' — exposes only the *_batch variant
      // to the agent so it doesn't have to choose between near-identical tools).
      const newTool: AgentTool = {
        type: 'registry',
        registry_model_id: model.model_id,
        registry_use_case: 'deduplicate',
        registry_call_style: 'batch',
      };
      onToolsChange([...selectedTools, newTool]);
    }
  };

  // Handle use case change for a model
  const handleUseCaseChange = (modelId: number, useCase: RegistryUseCase, e: React.MouseEvent | React.ChangeEvent) => {
    e.stopPropagation(); // Don't trigger model toggle
    const updatedTools = selectedTools.map((tool) => {
      if (tool.type === 'registry' && tool.registry_model_id === modelId) {
        return { ...tool, registry_use_case: useCase };
      }
      return tool;
    });
    onToolsChange(updatedTools);
  };

  // Toggle tool list visibility for a model
  const toggleToolList = (modelId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't trigger model toggle
    setExpandedToolLists((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) {
        next.delete(modelId);
      } else {
        next.add(modelId);
      }
      return next;
    });
  };

  // Toggle schema details for an individual injected tool
  const toggleToolSchema = (modelId: number, toolName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const key = `${modelId}:${toolName}`;
    setExpandedToolSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-3" data-testid="registry-tool-selector">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Registry Tools</h4>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Loading registry-enabled models...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-3" data-testid="registry-tool-selector">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Registry Tools</h4>
        </div>
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      </div>
    );
  }

  // Empty state - hide entirely when no registry models exist
  if (registryModels.length === 0) {
    return null;
  }

  // Main render with models
  return (
    <div className="space-y-3" data-testid="registry-tool-selector">
      <div className="flex items-center gap-2">
        <Database className="w-5 h-5 text-gray-500 dark:text-gray-400" data-testid="database-icon" />
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Registry Tools</h4>
      </div>

      {/* Selected count summary */}
      {selectedRegistryCount > 0 && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Selected: {selectedRegistryCount} registry model(s)
        </div>
      )}

      {/* Model cards */}
      <div className="space-y-2">
        {registryModels.map((model) => {
          const isSelected = isModelSelected(model.model_id);

          return (
            <div
              key={model.model_id}
              onClick={() => handleModelToggle(model)}
              className={`p-3 border rounded-lg cursor-pointer transition-all ${
                isSelected
                  ? 'border-blue-500 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-cap-dark hover:border-gray-400'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Model name */}
                  <div className="font-medium text-gray-900 dark:text-warm-white">
                    {model.class_name}
                  </div>

                  {/* Model description */}
                  {model.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {model.description}
                    </div>
                  )}

                  {/* Dedup fields */}
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {model.registry_dedup_fields && model.registry_dedup_fields.length > 0 ? (
                      <span>Dedup: {model.registry_dedup_fields.join(', ')}</span>
                    ) : (
                      <span>No dedup fields</span>
                    )}
                  </div>

                  {/* Entry count */}
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {model.entry_count} entries
                  </div>
                </div>

                {/* Checkbox indicator */}
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ml-3 ${
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-cap-dark'
                  }`}
                >
                  {isSelected && <Check className="w-4 h-4" data-testid="check-icon" />}
                </div>
              </div>

              {/* Use case selector and injected tools list - shown when selected */}
              {isSelected && (
                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                  {/* Use case dropdown */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Use Case
                    </label>
                    <div onClick={(e) => e.stopPropagation()}>
                      <CustomSelect
                        value={getModelUseCase(model.model_id)}
                        onChange={(value) => {
                          const updatedTools = selectedTools.map((tool) => {
                            if (tool.type === 'registry' && tool.registry_model_id === model.model_id) {
                              return { ...tool, registry_use_case: value as RegistryUseCase };
                            }
                            return tool;
                          });
                          onToolsChange(updatedTools);
                        }}
                        options={USE_CASE_OPTIONS.map((option) => ({
                          value: option.value,
                          label: option.label,
                          description: option.description,
                        }))}
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white px-2 py-1 bg-white dark:bg-cap-dark focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {USE_CASE_OPTIONS.find(o => o.value === getModelUseCase(model.model_id))?.description}
                    </p>
                  </div>

                  {/* Call style toggle - hidden for 'full' use case which exposes all tools */}
                  {getModelUseCase(model.model_id) !== 'full' && (
                    <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Call Style
                      </label>
                      <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden" role="radiogroup" aria-label="Registry tool call style">
                        {(['batch', 'single'] as RegistryCallStyle[]).map((style) => {
                          const active = getModelCallStyle(model.model_id) === style;
                          return (
                            <button
                              key={style}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              data-testid={`call-style-${style}-${model.model_id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const updatedTools = selectedTools.map((tool) => {
                                  if (tool.type === 'registry' && tool.registry_model_id === model.model_id) {
                                    return { ...tool, registry_call_style: style };
                                  }
                                  return tool;
                                });
                                onToolsChange(updatedTools);
                              }}
                              className={`px-3 py-1 text-xs font-medium transition-colors ${
                                active
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-white dark:bg-cap-dark text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }`}
                            >
                              {style === 'batch' ? 'Batch (recommended)' : 'Single'}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {getModelCallStyle(model.model_id) === 'batch'
                          ? 'Agent receives only the batch tool. Wrap a single item in an array of length 1 to register one entry.'
                          : 'Agent receives only the single-item tool. Use this only when batch is awkward; the agent will need to call once per entry.'}
                      </p>
                    </div>
                  )}

                  {/* Tools list toggle */}
                  <button
                    type="button"
                    onClick={(e) => toggleToolList(model.model_id, e)}
                    className="flex items-center gap-1 text-xs text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 font-medium"
                  >
                    <Wrench className="w-3 h-3" />
                    <span>{getInjectedTools(model.class_name, getModelUseCase(model.model_id), model.registry_dedup_fields, getModelCallStyle(model.model_id)).length} tools injected</span>
                    {expandedToolLists.has(model.model_id) ? (
                      <ChevronDown className="w-3 h-3" />
                    ) : (
                      <ChevronRight className="w-3 h-3" />
                    )}
                  </button>

                  {expandedToolLists.has(model.model_id) && (
                    <div className="mt-2 space-y-2">
                      {getInjectedTools(model.class_name, getModelUseCase(model.model_id), model.registry_dedup_fields, getModelCallStyle(model.model_id)).map((tool) => {
                        const schemaKey = `${model.model_id}:${tool.name}`;
                        const schemaExpanded = expandedToolSchemas.has(schemaKey);
                        return (
                          <div key={tool.name} className="text-xs pl-4">
                            <div className="flex items-baseline gap-1 flex-wrap">
                              <code className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-1 py-0.5 rounded font-mono text-[10px]">
                                {tool.name}
                              </code>
                              <span className="text-gray-500 dark:text-gray-400">{tool.desc}</span>
                              <button
                                type="button"
                                onClick={(e) => toggleToolSchema(model.model_id, tool.name, e)}
                                className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 font-medium"
                                aria-expanded={schemaExpanded}
                                data-testid={`toggle-schema-${tool.name}`}
                              >
                                {schemaExpanded ? (
                                  <ChevronDown className="w-3 h-3" />
                                ) : (
                                  <ChevronRight className="w-3 h-3" />
                                )}
                                <span>Schema</span>
                              </button>
                            </div>
                            {schemaExpanded && (
                              <div className="mt-1 space-y-1.5" data-testid={`schema-details-${tool.name}`}>
                                <div>
                                  <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 mb-0.5">
                                    Input (what the agent sends)
                                  </p>
                                  <pre className="text-[10px] bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                                    {JSON.stringify(tool.inputSchema, null, 2)}
                                  </pre>
                                </div>
                                <div>
                                  <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 mb-0.5">
                                    Example output (what the agent receives)
                                  </p>
                                  <pre className="text-[10px] bg-gray-800 text-gray-100 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                                    {JSON.stringify(tool.outputExample, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RegistryToolSelector;
